import React, { useMemo } from 'react';
import { OrganizationTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { RouteProp } from '@react-navigation/native';
import useOrganizationPaymentIntegrationBranding from '../../../../../hooks/api/useOrganizationPaymentIntegrationBranding';
import Text from '../../../../v2BaseComponents/Text';
import {
    TempAndPersistedAvatarState,
    avatarStateToAvatarChange,
} from '../../../../v2BaseComponents/ImagePicker/TempImagePicker';
import { Form } from '../../../../v2BaseComponents/Form';
import { APITypes } from '../../../../../primary-record-types';
import Stack, { FormStack } from '../../../../v2BaseComponents/Stack';
import MaybeStackedImageField from '../../../../shared/MaybeStackedImageField';
import { getIntl } from '../../../../../utils/intl';
import { Field } from 'formik';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import urls from '../../../../../utils/urls';
import { useOrganizationId } from '../../../../../hooks/api/useOrganization';
import { AVATAR_UPLOAD_QUALITY } from '../../../../../utils/constants';
import { ContentTypeValidation, DimensionValidation } from '../../../../v2BaseComponents/ImagePicker';

const intl = getIntl('EditClientBillingBrandingScreen', {
    organizationLogoMainHelpText: 'Primary Organization Logo',
    organizationLogoSubHelpText:
        'Must be a PNG or JPG file, square or rectangular, with dimensions of at least 128px by 128px',
    organizationIconMainHelpText: 'Organization Icon',
    organizationIconSubHelpText:
        'Must be a PNG or JPG file, square in shape, with dimensions of at least 128px by 128px',
    organizationLogo: 'Logo',
    icon: 'Icon',
    addLogo: 'Add Logo',
    addIcon: 'Add Icon',
    logoDimensionError: 'Must be at least 128px wide and 128px tall',
    iconDimensionError: 'Avatar must be square and at least 128px wide and 128px tall',
    editStripeBranding: 'Edit Stripe Branding',
    contentTypeError: 'Invalid file type.  Please upload a JPG or PNG file.',
});

export type EditClientBillingBrandingScreenOwnProps = {};

export type EditClientBillingBrandingScreenProps = EditClientBillingBrandingScreenOwnProps & {
    route: RouteProp<OrganizationTabParamList, 'edit-invitation-template'>;
};

type FormValue = {
    logo: TempAndPersistedAvatarState | null;
    icon: TempAndPersistedAvatarState | null;
};

const formValueToUpdate = (
    values: FormValue,
): APITypes.organizationService.UpdateOrganizationPaymentIntegrationImagesRequest => {
    return {
        logo: avatarStateToAvatarChange(values.logo),
        icon: avatarStateToAvatarChange(values.icon),
    };
};

const organizationIconDimensionValidator: DimensionValidation = ({ width, height }) => {
    const isValid = width >= 128 && height >= 128 && width === height;

    if (!isValid) {
        return intl('iconDimensionError');
    }
};

const organizationLogoDimensionValidator: DimensionValidation = ({ width, height }) => {
    const isValid = width >= 128 && height >= 128;

    if (!isValid) {
        return intl('logoDimensionError');
    }
};

// ensure mime type is JPG or PNG
// HEIC is also allowed because it will get converted to PNG
const allowedContentTypes = new Set(['image/jpeg', 'image/png', 'image/jpg', 'image/heic', 'image/heif']);
const contentTypeValidation: ContentTypeValidation = (contentType) => {
    if (!allowedContentTypes.has(contentType)) {
        return intl('contentTypeError');
    }
};

const FormContent: React.FC<{
    initialValues: FormValue;
    onCancel: () => void;
    onSubmit: (update: APITypes.organizationService.UpdateOrganizationPaymentIntegrationImagesRequest) => void;
}> = ({ initialValues, onCancel, onSubmit }) => {
    const organizationId = useOrganizationId();

    return (
        <Form
            actionContainerSubmit
            initialValues={initialValues}
            onCancel={onCancel}
            cancelButtonVariant="secondary"
            onSubmit={(values) => {
                onSubmit(formValueToUpdate(values as FormValue));
            }}
        >
            {({}) => (
                <FormStack>
                    <Stack horizontal fullWidth justify="center">
                        <Text variant="h4" alignText="center">
                            {intl('editStripeBranding')}
                        </Text>
                    </Stack>
                    <MaybeStackedImageField
                        helpTextMain={intl('organizationLogoMainHelpText')}
                        subHelpText={intl('organizationLogoSubHelpText')}
                    >
                        <Field
                            name="logo"
                            component={FormikInputs.TempAndExistingImagePicker}
                            existingAvatarUrl={urls.api.organizations.paymentIntegration.brandingImage(
                                organizationId,
                                'logo',
                            )}
                            title={intl('organizationLogo')}
                            imagePickerOptions={{
                                allowsEditing: true,
                                quality: AVATAR_UPLOAD_QUALITY,
                            }}
                            sizeVariant="logo"
                            addPhotoText={intl('addLogo')}
                            dimensionValidation={organizationLogoDimensionValidator}
                            contentTypeValidation={contentTypeValidation}
                        />
                    </MaybeStackedImageField>
                    <MaybeStackedImageField
                        helpTextMain={intl('organizationIconMainHelpText')}
                        subHelpText={intl('organizationIconSubHelpText')}
                    >
                        <Field
                            name="icon"
                            component={FormikInputs.TempAndExistingImagePicker}
                            existingAvatarUrl={urls.api.organizations.paymentIntegration.brandingImage(
                                organizationId,
                                'icon',
                            )}
                            title={intl('icon')}
                            imagePickerOptions={{
                                allowsEditing: true,
                                quality: AVATAR_UPLOAD_QUALITY,
                            }}
                            sizeVariant="icon"
                            addPhotoText={intl('addIcon')}
                            dimensionValidation={organizationIconDimensionValidator}
                            contentTypeValidation={contentTypeValidation}
                        />
                    </MaybeStackedImageField>
                </FormStack>
            )}
        </Form>
    );
};

const EditClientBillingBrandingScreen: React.FC<EditClientBillingBrandingScreenProps> = (_props) => {
    const goBack = useGoBack();
    const { isLoadingIconBranding, isLoadingLogoBranding, isUpdatingBranding, updateBranding } =
        useOrganizationPaymentIntegrationBranding({});
    const initialValues: FormValue = useMemo(
        () => ({
            logo: null,
            icon: null,
        }),
        [],
    );
    return (
        <AnimateLoadedContent isLoading={isLoadingIconBranding || isLoadingLogoBranding || isUpdatingBranding}>
            {() => (
                <FormContent
                    initialValues={initialValues}
                    onCancel={goBack}
                    onSubmit={(values) => {
                        updateBranding(values);
                    }}
                />
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(EditClientBillingBrandingScreen);
